import { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '../components/ui/dialog';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table';
import { getFornitori, createFornitore, updateFornitore, deleteFornitore } from '../lib/api';
import { useAuth } from '../context/AuthContext';
import { toast } from 'sonner';
import { Plus, Pencil, Trash2, Phone, Mail, MapPin, Users } from 'lucide-react';

const FornitoreForm = ({ fornitore, onSubmit, onCancel }) => {
  const [formData, setFormData] = useState({
    nome: fornitore?.nome || '',
    telefono: fornitore?.telefono || '',
    email: fornitore?.email || '',
    indirizzo: fornitore?.indirizzo || '',
    note: fornitore?.note || ''
  });
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.nome || !formData.telefono) {
      toast.error('Nome e telefono sono obbligatori');
      return;
    }
    setLoading(true);
    try {
      await onSubmit(formData);
    } finally {
      setLoading(false);
    }
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div className="space-y-2">
          <Label htmlFor="nome">Nome *</Label>
          <Input
            id="nome"
            value={formData.nome}
            onChange={(e) => setFormData({ ...formData, nome: e.target.value })}
            placeholder="Nome fornitore"
            data-testid="fornitore-nome"
          />
        </div>
        <div className="space-y-2">
          <Label htmlFor="telefono">Telefono WhatsApp *</Label>
          <Input
            id="telefono"
            value={formData.telefono}
            onChange={(e) => setFormData({ ...formData, telefono: e.target.value })}
            placeholder="+39 333 1234567"
            data-testid="fornitore-telefono"
          />
        </div>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div className="space-y-2">
          <Label htmlFor="email">Email</Label>
          <Input
            id="email"
            type="email"
            value={formData.email}
            onChange={(e) => setFormData({ ...formData, email: e.target.value })}
            placeholder="email@fornitore.it"
            data-testid="fornitore-email"
          />
        </div>
        <div className="space-y-2">
          <Label htmlFor="indirizzo">Indirizzo</Label>
          <Input
            id="indirizzo"
            value={formData.indirizzo}
            onChange={(e) => setFormData({ ...formData, indirizzo: e.target.value })}
            placeholder="Via Roma, 1"
            data-testid="fornitore-indirizzo"
          />
        </div>
      </div>
      <div className="space-y-2">
        <Label htmlFor="note">Note</Label>
        <Textarea
          id="note"
          value={formData.note}
          onChange={(e) => setFormData({ ...formData, note: e.target.value })}
          placeholder="Note aggiuntive..."
          rows={3}
          data-testid="fornitore-note"
        />
      </div>
      <div className="flex justify-end gap-2 pt-4">
        <Button type="button" variant="outline" onClick={onCancel}>
          Annulla
        </Button>
        <Button type="submit" disabled={loading} data-testid="fornitore-submit">
          {loading ? 'Salvataggio...' : fornitore ? 'Aggiorna' : 'Crea'}
        </Button>
      </div>
    </form>
  );
};

const Fornitori = () => {
  const [fornitori, setFornitori] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingFornitore, setEditingFornitore] = useState(null);
  const { user } = useAuth();
  const isAdmin = user?.role === 'admin';

  useEffect(() => {
    loadFornitori();
  }, []);

  const loadFornitori = async () => {
    try {
      const data = await getFornitori();
      setFornitori(data);
    } catch (error) {
      toast.error('Errore nel caricamento fornitori');
    } finally {
      setLoading(false);
    }
  };

  const handleCreate = async (data) => {
    try {
      await createFornitore(data);
      toast.success('Fornitore creato!');
      setDialogOpen(false);
      loadFornitori();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Errore nella creazione');
    }
  };

  const handleUpdate = async (data) => {
    try {
      await updateFornitore(editingFornitore.id, data);
      toast.success('Fornitore aggiornato!');
      setDialogOpen(false);
      setEditingFornitore(null);
      loadFornitori();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Errore nell\'aggiornamento');
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Sei sicuro di voler eliminare questo fornitore?')) return;
    try {
      await deleteFornitore(id);
      toast.success('Fornitore eliminato!');
      loadFornitori();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Errore nell\'eliminazione');
    }
  };

  const openEdit = (fornitore) => {
    setEditingFornitore(fornitore);
    setDialogOpen(true);
  };

  const closeDialog = () => {
    setDialogOpen(false);
    setEditingFornitore(null);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6 animate-fadeIn" data-testid="fornitori-page">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold">Fornitori</h1>
          <p className="text-muted-foreground mt-1">
            {isAdmin ? 'Gestisci i tuoi fornitori' : 'Visualizza fornitori (sola lettura)'}
          </p>
        </div>
        {isAdmin && (
          <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
            <DialogTrigger asChild>
              <Button onClick={() => setEditingFornitore(null)} data-testid="add-fornitore-btn">
                <Plus className="h-4 w-4 mr-2" />
                Nuovo Fornitore
              </Button>
            </DialogTrigger>
            <DialogContent className="sm:max-w-[600px]">
              <DialogHeader>
                <DialogTitle>
                  {editingFornitore ? 'Modifica Fornitore' : 'Nuovo Fornitore'}
                </DialogTitle>
                <DialogDescription>
                  {editingFornitore ? 'Modifica i dati del fornitore' : 'Inserisci i dati del nuovo fornitore'}
                </DialogDescription>
              </DialogHeader>
              <FornitoreForm
                fornitore={editingFornitore}
                onSubmit={editingFornitore ? handleUpdate : handleCreate}
                onCancel={closeDialog}
              />
            </DialogContent>
          </Dialog>
        )}
      </div>

      {fornitori.length === 0 ? (
        <Card>
          <CardContent className="flex flex-col items-center justify-center py-12">
            <Users className="h-12 w-12 text-muted-foreground mb-4" />
            <p className="text-muted-foreground">Nessun fornitore registrato</p>
            <p className="text-sm text-muted-foreground">Clicca su "Nuovo Fornitore" per iniziare</p>
          </CardContent>
        </Card>
      ) : (
        <Card>
          <CardContent className="p-0">
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Nome</TableHead>
                    <TableHead>Telefono</TableHead>
                    <TableHead className="hidden md:table-cell">Email</TableHead>
                    <TableHead className="hidden lg:table-cell">Indirizzo</TableHead>
                    <TableHead className="text-right">Azioni</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {fornitori.map((fornitore) => (
                    <TableRow key={fornitore.id} data-testid={`fornitore-row-${fornitore.id}`}>
                      <TableCell className="font-medium">{fornitore.nome}</TableCell>
                      <TableCell>
                        <a 
                          href={`tel:${fornitore.telefono}`}
                          className="flex items-center gap-1 text-primary hover:underline font-mono"
                        >
                          <Phone className="h-3 w-3" />
                          {fornitore.telefono}
                        </a>
                      </TableCell>
                      <TableCell className="hidden md:table-cell">
                        {fornitore.email && (
                          <a 
                            href={`mailto:${fornitore.email}`}
                            className="flex items-center gap-1 text-muted-foreground hover:text-foreground"
                          >
                            <Mail className="h-3 w-3" />
                            {fornitore.email}
                          </a>
                        )}
                      </TableCell>
                      <TableCell className="hidden lg:table-cell">
                        {fornitore.indirizzo && (
                          <span className="flex items-center gap-1 text-muted-foreground">
                            <MapPin className="h-3 w-3" />
                            {fornitore.indirizzo}
                          </span>
                        )}
                      </TableCell>
                      <TableCell className="text-right">
                        {isAdmin ? (
                          <div className="flex justify-end gap-2">
                            <Button
                              variant="ghost"
                              size="icon"
                              onClick={() => openEdit(fornitore)}
                              data-testid={`edit-fornitore-${fornitore.id}`}
                            >
                              <Pencil className="h-4 w-4" />
                            </Button>
                            <Button
                              variant="ghost"
                              size="icon"
                              className="text-destructive hover:text-destructive"
                              onClick={() => handleDelete(fornitore.id)}
                              data-testid={`delete-fornitore-${fornitore.id}`}
                            >
                              <Trash2 className="h-4 w-4" />
                            </Button>
                          </div>
                        ) : (
                          <span className="text-xs text-muted-foreground">Sola lettura</span>
                        )}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
};

export default Fornitori;
